import type { ReactNode } from 'react';
import { Fragment } from 'react';
import NextLink from 'next/link';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@savvy-kit/components/breadcrumb';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { isNil } from 'es-toolkit';

export type BreadcrumbSegment = {
  readonly label: string;
  readonly href?: string;
  readonly onClick?: () => void;
  /** When true, render a skeleton placeholder in place of the label (for async-loaded names). */
  readonly loading?: boolean;
  /** Optional secondary text rendered after the label in muted style (e.g. masked account numbers). */
  readonly suffix?: ReactNode;
};

const renderSegmentLabel = (segment: BreadcrumbSegment) => {
  if (segment.loading) return <Skeleton className="h-4 w-24 inline-block align-middle" />;
  if (segment.suffix) {
    return (
      <>
        {segment.label}
        <span className="ml-1 text-secondary">{segment.suffix}</span>
      </>
    );
  }
  return segment.label;
};

type BreadcrumbSegmentsProps = {
  readonly segments: readonly BreadcrumbSegment[];
  /** Set false when something outside the list owns `aria-current="page"` (e.g. PageTitle's H1 above a parent-only trail). */
  readonly markLastAsCurrent?: boolean;
};

const renderSegmentContent = (segment: BreadcrumbSegment, isCurrent: boolean) => {
  const label = renderSegmentLabel(segment);
  if (!isCurrent && !segment.loading && !isNil(segment.href)) {
    return <BreadcrumbLink render={<NextLink href={segment.href} />}>{label}</BreadcrumbLink>;
  }
  if (!isCurrent && !segment.loading && !isNil(segment.onClick)) {
    return (
      <button
        type="button"
        onClick={segment.onClick}
        className="transition-colors hover:text-foreground text-sm cursor-pointer"
      >
        {label}
      </button>
    );
  }
  if (isCurrent) return <BreadcrumbPage>{label}</BreadcrumbPage>;
  return <span>{label}</span>;
};

function BreadcrumbSegments({ segments, markLastAsCurrent = true }: BreadcrumbSegmentsProps) {
  if (segments.length === 0) return null;
  return (
    <Breadcrumb>
      <BreadcrumbList>
        {segments.map((segment, i) => {
          const isCurrent = markLastAsCurrent && i === segments.length - 1;
          return (
            <Fragment key={`${i}-${segment.href ?? segment.label}`}>
              {i > 0 && <BreadcrumbSeparator />}
              <BreadcrumbItem>{renderSegmentContent(segment, isCurrent)}</BreadcrumbItem>
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

export default BreadcrumbSegments;
